import { api } from '@/api';
import {
  DeviceMeasurementErrorDto,
  instanceOfPtwErrorExceptionDetails,
  instanceOfRunBeamConstancyMeasurementRequestDto,
  MeasurementStatusDto,
  ResultDto,
} from '@/api/generator';
import { NotificationEntry } from '@/components/notifications/types';
import { notificationForDeviceError } from '@/components/notifications/utils';
import { injectStrict } from '@/utils/injectStrict';
import { waitForConnectionState } from '@/utils/signalR';
import { HubConnectionBuilder, HubConnectionState, ISubscription } from '@microsoft/signalr';
import { useMemoize, createEventHook } from '@vueuse/core';
import {
  computed,
  MaybeRefOrGetter,
  onMounted,
  onUnmounted,
  Ref,
  ref,
  toRef,
  toValue,
  watch,
  watchEffect,
} from 'vue';
import { MeasurementEssentials } from '../types';
import { WorkflowCommunicationHubKey } from './types';
import { severityOrder, MAX_ACCEPTABLE_SEVERITY } from '@/components/deviceStatus/utils/severity';
import { truthy } from '@/utils/helper';
import { isSemanticError } from '@/errorHandling';

// Check if measurement has errors with severity too high to accept
export function hasUnacceptableErrors(messages: DeviceMeasurementErrorDto[]): boolean {
  if (!messages?.length) return false;

  return messages.some((message) => {
    if (!message.severity) return false;

    const maxAcceptableSeverityLevel = severityOrder.indexOf(MAX_ACCEPTABLE_SEVERITY);
    const severityIndex = severityOrder.indexOf(message.severity);
    // severityOrder is sorted from highest to lowest severity
    return severityIndex >= 0 && severityIndex < maxAcceptableSeverityLevel;
  });
}

export function getWorkflowCommunicationHubConnectionSignalR() {
  const interval = 1; // in seconds

  const connection = new HubConnectionBuilder()
    .withUrl('/workflowCommunicationHub')
    .withAutomaticReconnect({
      nextRetryDelayInMilliseconds: () => interval * 1000,
    })
    .build();
  return connection;
}

export function useMeasurement<T extends MeasurementEssentials>(
  measurement: MaybeRefOrGetter<T | undefined>,
) {
  const measurementRef = toRef(measurement);

  const measurementId = ref<string | undefined>(undefined);

  const streamId = ref<string | undefined>(undefined);

  const status = useMeasurementStatusSignalR(streamId);

  const finishHook = createEventHook<ResultDto | undefined>();

  const messages = ref<DeviceMeasurementErrorDto[]>([]);

  const startMeasurementError = ref<DeviceMeasurementErrorDto>();

  watch(
    () => status.value?.messages,
    (newMessages) => {
      if (newMessages) {
        messages.value = newMessages;
      }
    },
    {
      immediate: true,
    },
  );

  const notifications = computed<NotificationEntry[]>(() => {
    return [startMeasurementError.value, ...messages.value].filter(truthy).map((message) => ({
      ...notificationForDeviceError(message),
      closable: false,
    }));
  });

  const isMeasuring = computed(() => {
    switch (status.value?.measurementStatus) {
      case 'BeamDetected':
      case 'ProcessingResults':
        return true;
      default:
        return false;
    }
  });

  const cancelable = computed(
    () => !!status.value?.measurementStatus && status.value?.measurementStatus !== 'Finished',
  );

  function startMeasurementErrorHandler(error: Error) {
    if (
      isSemanticError(error) &&
      instanceOfPtwErrorExceptionDetails(error.errorResponse?.details)
    ) {
      startMeasurementError.value = error.errorResponse.details.error;
    } else {
      return error;
    }
  }

  async function start() {
    const runMeasurementRequestDto = toValue(measurement)?.request;

    if (runMeasurementRequestDto) {
      const { result } = await api.measurementsApi.runMeasurement(
        {
          runMeasurementRequestDto,
        },
        [startMeasurementErrorHandler],
      );

      streamId.value = result?.streamId;

      if (result && measurementRef.value) {
        measurementId.value = result.measurementId;
        startMeasurementError.value = undefined;
        return measurementId.value;
      }
    }
  }

  async function getResult() {
    if (measurementId.value) {
      const { result: measurementResult } = await api.resultsApi.getResultByMeasurementId({
        measurementId: measurementId.value,
      });

      return measurementResult;
    }
  }

  let startPromise: Promise<string | undefined> | undefined;

  async function safeStart() {
    startPromise = start();
    await startPromise;
  }

  async function safeCancel() {
    return startPromise?.then(cancel);
  }

  async function cancel(measurementId: string | undefined) {
    if (measurementId) {
      const { success } = await api.measurementsApi.stopMeasurement({
        measurementId: measurementId,
      });

      return success;
    } else {
      console.warn('Unable to cancel measurement, no measurementId, start might have failed');
    }
  }

  function reset() {
    streamId.value = undefined;
    measurementId.value = undefined;
    messages.value = [];
  }

  watch(
    () => status.value?.measurementStatus,
    async (newStatus) => {
      if (newStatus === 'Finished') {
        const measurementResult =
          measurementRef.value?.request &&
          instanceOfRunBeamConstancyMeasurementRequestDto(measurementRef.value.request)
            ? await getResult()
            : undefined;

        finishHook.trigger(measurementResult);
      }
    },
  );

  return {
    start: safeStart,
    cancel: safeCancel,
    reset,
    cancelable,
    status,
    isMeasuring,
    notifications,
    messages,
    onFinish: finishHook.on,
    measurement: measurementRef,
    measurementId,
    request: computed(() => toValue(measurement)?.request),
  };
}

const cachedStateValue = useMemoize((_deviceId: string | undefined) => {
  return ref<MeasurementStatusDto | undefined>();
});

function useMeasurementStatusSignalR(
  streamId: MaybeRefOrGetter<string | undefined>,
): Ref<MeasurementStatusDto | undefined> {
  const connection = injectStrict(WorkflowCommunicationHubKey);

  let subscription: ISubscription<MeasurementStatusDto> | undefined;

  const id = toRef(streamId);

  watchEffect(async () => {
    if (id.value) {
      await subscribe(id.value);
    } else {
      subscription?.dispose();
    }
  });

  async function subscribe(subscriptionId: string) {
    if (subscription) {
      subscription.dispose();
    }

    await waitForConnectionState(connection, HubConnectionState.Connected);

    subscription = connection
      .stream<MeasurementStatusDto>('RegisterToWorkflow', subscriptionId)
      .subscribe({
        next: (value) => {
          if (value.workflowId) {
            cachedStateValue(value.workflowId).value = value;
          }
        },
        error: (value) => {
          throw new Error('Not able to obtain measurement status', value);
        },
        complete: () => {
          throw new Error('Stream was completed unexpectedly');
        },
      });
  }

  onMounted(async () => {
    if (connection.state === HubConnectionState.Disconnected) {
      await connection.start();
    }
  });

  onUnmounted(async () => {
    subscription?.dispose();
    subscription = undefined;
  });

  return computed(() => {
    return cachedStateValue(id.value).value;
  });
}
